import { processFormThumbnail } from "@utils/image-upload";
import { ICONS } from "@assets/icons";
import Button from "@components/atoms/Button";
import { type BasicOptionType } from "@components/atoms/CheckBox";
import ContentBox from "@components/atoms/ContentBox";
import IconBox from "@components/atoms/IconBox";
import RenderBannerExposureLocationCheckbox from "@components/molecules/RenderBannerExposureLocationCheckbox";
import RenderBannerConnectionList from "@components/molecules/RenderBannerConnectionList";
import SizeNoticeBox from "@components/molecules/SizeNoticeBox";
import regex from "@constants/regex";
import {
    type V2BannerUpdateRequestDto,
    useV2BannersControllerFetchBannerDetail,
    useV2BannersControllerUpdateBanner,
    useV2BannersControllerDeleteBanner,
    V2BannerUpdateRequestDtoCategory as BannerCategory,
    V2BannerUpdateRequestDtoLinkType as LinkType,
    type V2BannerUpdateRequestDtoPositionsItem,
} from "@everon-it/api-client/contents-gw";
import useGlobalModal from "@hooks/useGlobalModal";
import useGlobalNoti from "@hooks/useGlobalNoti";
import useRouter from "@hooks/useRouter";
import useAuthStore from "@store/auth";
import {
    ContentsHeader,
    ContentsMainWrap,
    ContentsWrap,
    FooterButtonWrap,
    HeaderTitle,
    HeaderWrap,
    InputContentsWrap,
    InputWrap,
    Label,
    LabelInnerWrap,
    LabelWrap,
    LeftButtonWrap,
    ConnectionCategory,
    RightButtonWrap,
} from "@pages/BannerCreate";
import { RenderCommonElementByType } from "@utils/DynamicComponentUtils";
import { compareObjects } from "@utils/data-fns";
import { useFormik } from "formik";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { BannerDetailData, type BannerDetailInitValuesType } from "./Data";

const BannerDetail: React.FC = () => {
    // Props
    const HEADER_TITLE = "배너 상세";
    const CONTENT_TITLE = "배너 내용";

    // Data
    const { formInitialValues, contentsOption } = BannerDetailData;

    // Hooks
    const { query, goBack } = useRouter();
    const { hideModal, showConfirmModal } = useGlobalModal();
    const { showEditNoti, showDeleteNoti } = useGlobalNoti();
    const { userInfo } = useAuthStore();
    const uuid = String(query.id || "");
    const [initData, setInitData] = useState<BannerDetailInitValuesType>();
    const [connectionKindList, setConnectionKindList] = useState<BasicOptionType<LinkType>>(ConnectionCategory[0]);

    // api-client: 상세 조회
    const { data: detailData } = useV2BannersControllerFetchBannerDetail(uuid, {
        query: {
            enabled: !!uuid,
            retry: false,
            refetchOnWindowFocus: false,
        },
    });

    // api-client: 수정 mutation
    const { mutate: editMutation } = useV2BannersControllerUpdateBanner({
        mutation: {
            onSuccess: () => {
                showEditNoti({});
                goBack();
            },
        },
    });

    // api-client: 삭제 mutation
    const { mutate: deleteMutate } = useV2BannersControllerDeleteBanner({
        mutation: {
            onSuccess: () => {
                showDeleteNoti({});
                goBack();
            },
        },
    });

    // Formik
    const formik = useFormik({
        initialValues: formInitialValues,
        onSubmit: async values => {
            // 이미지 업로드 처리
            const imageUrl = await processFormThumbnail(values.imageUrl, "banner");

            // positions에서 "ALL" 제외
            const filteredPositions = values.positions.filter((p): p is V2BannerUpdateRequestDtoPositionsItem => p !== "ALL");

            const body: V2BannerUpdateRequestDto = {
                category: values.category,
                displayStartAt: values.displayStartAt,
                displayEndAt: values.displayEndAt,
                isVisible: values.isVisible,
                title: values.title,
                imageUrl: imageUrl,
                linkType: values.linkType,
                linkTargetId: values.linkTargetId ?? undefined,
                linkUrl: values.linkUrl || undefined,
                positions: filteredPositions,
                displayOrder: values.displayOrder,
                updatedBy: userInfo?.name ?? "",
            };
            editMutation({ uuid, data: body });
        },
    });

    // 상세 데이터 formik에 반영
    useEffect(() => {
        if (detailData?.row) {
            const row = detailData.row;
            const customData: BannerDetailInitValuesType = {
                category: row.category,
                displayStartAt: row.displayStartAt ?? "",
                displayEndAt: row.displayEndAt ?? "",
                isVisible: row.isVisible,
                title: row.title,
                imageUrl: row.imageUrl ?? "",
                linkType: row.linkType ?? LinkType.NOTICE,
                linkTargetId: row.linkTargetId ?? null,
                linkUrl: row.linkUrl ?? "",
                displayOrder: row.displayOrder ?? 0,
                positions: row.positions ?? [],
                linkTargetTitle: row.linkTargetTitle ?? null,
                linkTargetDisplayStartAt: row.linkTargetDisplayStartAt ?? undefined,
                linkTargetDisplayEndAt: row.linkTargetDisplayEndAt ?? undefined,
            };
            setInitData(customData);
            void formik.setValues(customData);
        }
    }, [detailData]);

    // Functions
    const handleGoBackClick = () => {
        if (JSON.stringify(initData) !== JSON.stringify(formik.values)) {
            showConfirmModal({
                title: `작성한 내용을 저장하지 않고\n 취소 하시겠어요?`,
                firstButtonText: "닫기",
                secondButtonText: "확인",
                onConfirm: () => {
                    hideModal();
                    goBack();
                },
            });
        } else {
            goBack();
        }
    };

    const handleEditContent = () => {
        showConfirmModal({
            title: `저장하시겠어요?`,
            firstButtonText: "닫기",
            secondButtonText: "저장하기",
            onConfirm: () => {
                hideModal();
                formik.handleSubmit();
            },
        });
    };

    const handleDeleteContent = () => {
        showConfirmModal({
            title: `삭제하시겠어요?`,
            firstButtonText: "닫기",
            secondButtonText: "삭제하기",
            onConfirm: () => {
                deleteMutate({ uuid });
                hideModal();
            },
        });
    };

    const isEditButtonValidate = useCallback(() => {
        if (!initData) {
            return true;
        }

        const { category, displayStartAt, displayEndAt, title, imageUrl, linkType, linkTargetId, linkUrl, positions } = formik.values;

        const isContentUrl = linkType === LinkType.URL;
        const isCompare = compareObjects(initData, formik.values);
        const urlRegex = regex.url;
        const isUrl = urlRegex.test(linkUrl); // http://, https://, app/ 포함 여부 확인

        const hasEmptyFields = !title || !displayStartAt || !displayEndAt || !imageUrl || (isContentUrl ? !linkUrl || !isUrl : !linkTargetId) || (category === BannerCategory.BANNER && !positions.length);

        return isCompare || hasEmptyFields;
    }, [formik.values, initData]);

    useEffect(() => {
        ConnectionCategory.forEach(item => {
            if (item.value === formik.values.linkType) {
                setConnectionKindList(item);
            }
        });
    }, [formik.values.linkType]);

    useEffect(() => {
        const { values, setValues, setFieldValue, initialValues } = formik;
        const { category, linkUrl, title } = values;
        const defaultLinkType = category === BannerCategory.BANNER ? LinkType.EVENT : LinkType.NOTICE;

        if (!initData) {
            return;
        }

        if (initData.category !== category) {
            void setValues({
                ...initialValues,
                displayStartAt: moment().format("YYYY-MM-DD"),
                category,
                linkType: defaultLinkType,
            });
        }

        if (linkUrl || title) {
            return;
        }
        void setFieldValue("linkType", defaultLinkType);
    }, [formik.values.category, initData]);

    return (
        <ContentsWrap>
            <HeaderWrap>
                <IconBox width={32} height={32} src={ICONS.ARROW_ICON.BACK} isCursorPointer onClick={handleGoBackClick} />
                <HeaderTitle>{HEADER_TITLE}</HeaderTitle>
            </HeaderWrap>
            <div>
                <ContentBox style={{ padding: 0 }}>
                    <ContentsHeader>{CONTENT_TITLE}</ContentsHeader>

                    <ContentsMainWrap>
                        {contentsOption?.map(({ label, name, options, hasKeywordToShowContent, isSizeQuestion, size, ...res }, _) => {
                            if (hasKeywordToShowContent) {
                                if (formik.values.category === hasKeywordToShowContent) {
                                    return <RenderBannerExposureLocationCheckbox key={`${_}_contents`} label={label} formik={formik} name={name} checkBoxOptions={options ?? []} />;
                                }
                            }
                            if (!hasKeywordToShowContent) {
                                const bannerSizeCheck = formik.values.category === BannerCategory.POPUP ? size : "small";
                                return (
                                    <React.Fragment key={`${_}_contents`}>
                                        <InputWrap>
                                            <LabelWrap>
                                                <LabelInnerWrap>
                                                    <Label>{label || "LABEL"}</Label>
                                                    {isSizeQuestion && <SizeNoticeBox size={(size = bannerSizeCheck)} />}
                                                </LabelInnerWrap>
                                            </LabelWrap>
                                            <InputContentsWrap>
                                                {RenderCommonElementByType({
                                                    formik,
                                                    name,
                                                    options,
                                                    size: bannerSizeCheck,
                                                    ...res,
                                                })}
                                            </InputContentsWrap>
                                        </InputWrap>
                                    </React.Fragment>
                                );
                            }
                        })}
                        {RenderBannerConnectionList({
                            formik,
                            connectionKindList,
                            setConnectionKindList,
                        })}
                    </ContentsMainWrap>
                </ContentBox>
                <FooterButtonWrap>
                    <LeftButtonWrap>
                        <Button text="삭제" buttonType="line_red" onClick={handleDeleteContent} />
                    </LeftButtonWrap>
                    <RightButtonWrap>
                        <Button text="취소" buttonType="line_black" onClick={handleGoBackClick} />
                        <Button disabled={isEditButtonValidate()} text="저장하기" onClick={handleEditContent} />
                    </RightButtonWrap>
                </FooterButtonWrap>
            </div>
        </ContentsWrap>
    );
};

export default BannerDetail;
